import { Link } from "react-router-dom";
import { formatDate, type SiteEvent } from "@/lib/events";

interface EventCardProps {
  event: SiteEvent;
  layout?: "vertical" | "horizontal";
  order?: number;
}

export default function EventCard({ event, layout = "vertical", order }: EventCardProps) {
  if (layout === "horizontal") {
    return (
      <article className="group border-b border-background-200 pb-6 last:border-b-0 last:pb-0">
        <Link to={`/events/${event.slug}`} className="flex flex-col gap-4 sm:flex-row sm:gap-6">
          <div className="relative h-44 w-full shrink-0 overflow-hidden rounded-lg bg-background-200 sm:h-36 sm:w-56">
            <img
              src={event.cover}
              alt={`${event.title} 复盘封面图`}
              title={`${event.title} - 网红事件完整经过`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-medium text-secondary-900">
                {event.categoryName}
              </span>
              <span className="text-foreground-400">{formatDate(event.publishedAt)}</span>
              {typeof order === "number" && (
                <span className="text-foreground-400">热度 {event.heat}</span>
              )}
            </div>
            <h3 className="mt-2.5 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
              {event.title}
            </h3>
            <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">
              {event.summary}
            </p>
            <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读约 {event.readTime} 分钟
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line"></i>
                {event.heat}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-flag-line"></i>
                {event.status}
              </span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/events/${event.slug}`} className="flex h-full flex-col">
        <div className="relative h-48 w-full overflow-hidden bg-background-200">
          <img
            src={event.cover}
            alt={`${event.title} 复盘封面图`}
            title={`${event.title} - 网红事件深度解析`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
          <span className="absolute left-3 top-3 rounded-full bg-foreground-950/75 px-2.5 py-1 text-xs font-medium text-background-50">
            {event.categoryName}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-5">
          <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {event.title}
          </h3>
          <p className="mt-2.5 line-clamp-3 text-sm leading-6 text-foreground-600">
            {event.summary}
          </p>
          <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {formatDate(event.publishedAt)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line"></i>
              {event.heat}
            </span>
            <span className="ml-auto flex items-center gap-1 text-primary-600">
              阅读复盘
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}